import stopLayer from '../helper/stopLayerRoute';
import MMapAbstract from './MMapAbstract';

class MMapRoute extends MMapAbstract {
  focusedRoute = null;

  hoveredFeatureId = null;

  duration = 1500;

  constructor(
    parentElement,
  ) {
    const center = JSON.parse(parentElement.dataset.center);
    if (window.matchMedia('(min-width: 1024px)').matches) {
      center[1] += 0.02;
    }

    super(parentElement, 10.95, center, ['stops', 'natural-point-label', 'poi-label', 'lines'], true);

    const buttonCloseMapElement = parentElement.querySelector('button[data-action="close-map"]');

    this.id = parentElement.closest('.o-route')?.id;

    // event listener
    parentElement.addEventListener('click', () => {
      if (!window.matchMedia('(min-width: 920px)').matches) {
        if (!parentElement.classList.contains('-extend')) {
          parentElement.classList.add('-extend');
          this.map.resize();
          if (this.userInteracted === false) {
            window.oTripRequest?.mTrips?.[0]?.showRoute();
          }
        }
      }
    });
    buttonCloseMapElement.addEventListener('click', (event) => {
      event.stopPropagation();
      parentElement.classList.remove('-extend');
      this.map.resize();
    });

    // init
    parentElement.appendChild(this.element);

    this.map.on('load', () => {
      this.createLayers();

      if (this.id) {
        this.addStops();
      } else {
        this.addAllRoutes();
        this.initRoutesMap();
      }
    });
  }

  initRoutesMap() {
    const observer = new IntersectionObserver(
      ([e]) => {
        e.target.classList.toggle('-sticky', e.intersectionRatio >= 1);
        this.map.resize();
      },
      { threshold: [1] },
    );

    observer.observe(this.parentElement);
  }

  onMouseenter() {
    // const feature = event.features[0];
    // if (feature.layer.id === 'lines' && feature.id) {
    //   this.hoveredFeatureId = feature.id;
    //   this.map.setFeatureState({ source: 'lines', id: feature.id }, {
    //     hover: true,
    //   });
    // }
    super.onMouseenter();
  }

  onMouseleave() {
    // if (this.hoveredFeatureId) {
    //   this.map.setFeatureState({ source: 'lines', id: this.hoveredFeatureId }, {
    //     hover: false,
    //   });
    // }
    super.onMouseleave();
  }

  onClick(event) {
    // Set `bbox` as 5px reactangle area around clicked point.
    const bbox = [
      [event.point.x - 2, event.point.y - 2],
      [event.point.x + 2, event.point.y + 2],
    ];
    // Find features intersecting the bounding box.
    const selectedFeatures = this.map.queryRenderedFeatures(bbox, {
      layers: this.clickableLayers,
    });
    const selectedFeature = selectedFeatures?.[0];
    if (selectedFeature) {
      if (selectedFeature.layer.id === 'lines') {
        window.location.href = `#${selectedFeature.properties.id}`;
      } else {
        super.onClick(event);
      }
    }
  }

  createLayers() {
    this.map.addSource('stops', {
      type: 'geojson',
      generateId: true,
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    stopLayer.source = 'stops';
    this.map.addLayer(stopLayer, 'settlement-major-label');

    this.map.addSource('lines', {
      type: 'geojson',
      generateId: true,
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    this.map.addLayer({
      id: 'lines',
      type: 'line',
      source: 'lines',
      layout: {
        'line-join': 'round',
      },
      paint: {
        'line-color': {
          type: 'identity',
          property: 'color',
        },
        'line-width': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          5, // Hovered line width
          3, // Original line width
        ],
        'line-opacity': [
          'case',
          ['boolean', ['feature-state', 'notFocused'], false],
          0.05,
          1,
        ],
      },
    }, 'road-label-simple');
  }

  onPopupButtonClick(event) {
    const { target } = event;
    const mPopupContentElement = target.closest('.m-popup-content');
    if (mPopupContentElement) {
      const { dataset } = mPopupContentElement;
      let location;
      if (dataset.stopId) {
        location = {
          type: 'stop',
          id: dataset.stopId,
          name: dataset.name,
        };
      } else if (dataset.geoPosition) {
        const geoPosition = dataset.geoPosition.split(',');
        location = {
          type: 'poiGeoPosition',
          geoPosition: {
            longitude: geoPosition[0],
            latitude: geoPosition[1],
          },
          name: dataset.name,
        };
      }
      if (target.dataset.action === 'set-from') {
        window.location = `/fahrplaene/verbindungsauskunft?from=${location.name}&fromData${JSON.stringify(location)}`;
      }
      if (target.dataset.action === 'set-to') {
        window.location = `/fahrplaene/verbindungsauskunft?to=${location.name}&fromData${JSON.stringify(location)}`;
      }
    }
  }

  async addAllRoutes() {
    const response = await fetch('/api/routes').then((_) => _.json());
    if (response.data) {
      const { data } = response;
      data.forEach((route) => {
        this.addRoute(route.id, route);
      });
    }
  }

  async addStops() {
    const { id } = this;
    const response = await fetch(`/api/routes/${id}`).then((_) => _.json());
    if (response.data) {
      const { data } = response;
      this.addRoute(id, data);

      const routeCoordinates = [];
      data.stops.forEach((stop) => {
        const coordinates = [
          parseFloat(stop.geoPosition.longitude),
          parseFloat(stop.geoPosition.latitude),
        ];
        routeCoordinates.push(coordinates);
      });
      this.fitCoordinates(routeCoordinates);
    }
  }

  addRoute(id, data) {
    let { color } = data;

    if (window.matchMedia('(prefers-contrast: more)').matches) {
      color = data.colorWCAG;
    }

    const routeCoordinates = [];
    const features = data.stops.map((stop) => {
      const { name, id: stopId, geoPosition } = stop;
      const coordinates = [
        parseFloat(geoPosition.longitude),
        parseFloat(geoPosition.latitude),
      ];
      routeCoordinates.push(coordinates);

      return {
        type: 'Feature',
        id: stopId,
        properties: {
          name,
          id: stopId,
          routeId: id,
          sizerank: 13,
          color,
        },
        geometry: {
          type: 'Point',
          coordinates,
        },
      };
    });

    this.map.getSource('stops').setData({
      type: 'FeatureCollection',
      // eslint-disable-next-line no-underscore-dangle
      features: [...this.map.getSource('stops')._data.features, ...features],
    });

    if (data.shape) {
      const coordinates = data.shape.map((item) => [
        parseFloat(item.geoPosition.longitude),
        parseFloat(item.geoPosition.latitude),
      ]);
      this.addLine(id, coordinates, color);
    } else {
      this.addLine(id, routeCoordinates, color);
    }
  }

  addLine(
    id = '',
    coordinates = [],
    color = '#000',
  ) {
    if (coordinates) {
      const features = [
        {
          type: 'Feature',
          id,
          properties: {
            id,
            color,
          },
          geometry: {
            type: 'LineString',
            coordinates,
          },
        },
      ];

      this.map.getSource('lines').setData({
        type: 'FeatureCollection',
        // eslint-disable-next-line no-underscore-dangle
        features: [...this.map.getSource('lines')._data.features, ...features],
      });
    }
  }

  focusRoute(routeId) {
    /* eslint-disable-next-line no-underscore-dangle */
    if (this.focusedRoute !== routeId) {
      window._paq?.push(['trackEvent', 'MMapRoute', 'focusRoute', routeId]);
    }

    this.focusedRoute = routeId;

    if (window.matchMedia('(min-width: 1024px)').matches) {
      this.map.setFilter('lines', ['==', 'id', routeId]);
      this.map.setFilter('stops', ['==', 'routeId', routeId]);

      // eslint-disable-next-line no-underscore-dangle
      const coordinates = this.map.getSource('lines')._data.features?.find((feature) => feature.properties.id === routeId)?.geometry.coordinates;

      if (coordinates) {
        this.boundsPadding.left = window.innerWidth * 0.5 + 40;
        this.fitCoordinates(coordinates, this.duration);
      }
    }
  }

  removeFocus() {
    this.focusedRoute = null;

    // remove filter, when there is no new focused route after 500ms
    setTimeout(() => {
      if (this.focusedRoute === null) {
        this.removeFilter();
      }
    }, 100);
  }

  removeFilter() {
    this.map.setFilter('lines', null);
    this.map.setFilter('stops', null);

    // reset to initial state
    this.boundsPadding.left = 40;
    this.map.flyTo({ center: this.center, zoom: this.zoom, duration: this.duration });
  }
}

export default MMapRoute;
